import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';
import { SeekopJwtTokenPayload } from '@core/interfaces/jwt.interface';
import { StorageService } from '@core/services/storages.service';

@Injectable({ providedIn: 'root' })
export class JwtService {
  private readonly jwtPayloadSubject = new BehaviorSubject<unknown | null>(null);

  constructor(
    private activatedRoute: ActivatedRoute,
    private storageService: StorageService
  ) {}

  public getJwt() {
    this.activatedRoute.queryParamMap.subscribe((params) => {
      const jwt = params.get('jwt');
      const refreshToken = params.get('refreshToken');

      if (jwt && refreshToken) {
        this.storageService.setSessionStorage('jwt', jwt);
        this.storageService.setSessionStorage('refreshToken', refreshToken);
        this.setToken(jwt);
      } else {
        this.loadTokenFromStorage();
      }
    });
  }

  private loadTokenFromStorage(): void {
    const jwt =
      this.storageService.getSessionStorage('jwt') ||
      this.storageService.getLocalStorage('token');

    if (jwt && typeof jwt === 'string') {
      this.setToken(jwt);
    }
  }

  public setToken<TPayload = SeekopJwtTokenPayload>(token: string): TPayload | null {
    const normalizedToken = token?.startsWith('Bearer ')
      ? token.replace('Bearer ', '')
      : token;

    try {
      const payload = jwtDecode<TPayload>(normalizedToken);
      this.jwtPayloadSubject.next(payload);
      return payload;
    } catch {
      this.jwtPayloadSubject.next(null);
      return null;
    }
  }

  public clearToken(): void {
    this.jwtPayloadSubject.next(null);
  }

  public getPayload<TPayload = SeekopJwtTokenPayload>(): TPayload | null {
    return (this.jwtPayloadSubject.value as TPayload) ?? null;
  }

  public getDealerId(): string {
    const payload = this.getPayload<SeekopJwtTokenPayload>();
    if (payload?.dealerId) {
      return payload.dealerId;
    }

    this.loadTokenFromStorage();
    return this.getPayload<SeekopJwtTokenPayload>()?.dealerId ?? '';
  }

  public payloadChanges<TPayload = SeekopJwtTokenPayload>() {
    return this.jwtPayloadSubject.asObservable() as unknown as import('rxjs').Observable<
      TPayload | null
    >;
  }
}
